import { useState, useCallback } from "react";
import { Image as ImageIcon, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: 1920)
 * @param {number} options.maxHeight - Maximum height (default: 1080)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<Blob>} - Compressed image blob
 */
const compressImage = (file, options = {}) => {
  const { maxWidth = 1920, maxHeight = 1080, quality = 0.8 } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Create canvas and draw resized image
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to compress image"));
          }
        },
        file.type === "image/png" ? "image/png" : "image/jpeg",
        quality
      );
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Convert a Blob/File to base64 data URL
 * @param {Blob|File} blob - The blob to convert
 * @returns {Promise<string>} - Base64 data URL
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * ImageUploader Component
 * 
 * A reusable image upload component with compression support.
 * 
 * @param {Object} props
 * @param {string} props.id - Unique ID for the input element
 * @param {string|null} props.preview - Preview URL (local blob URL or server URL)
 * @param {function} props.onImageSelect - Callback when image is selected: (file, previewUrl) => void
 * @param {function} props.onImageRemove - Callback when image is removed: () => void
 * @param {boolean} props.disabled - Whether the uploader is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.maxSizeMB - Maximum file size in MB (default: 5)
 * @param {number} props.maxWidth - Maximum image width for compression (default: 1920)
 * @param {number} props.maxHeight - Maximum image height for compression (default: 1080)
 * @param {number} props.quality - JPEG compression quality 0-1 (default: 0.8)
 * @param {boolean} props.compress - Whether to compress images (default: true)
 * @param {string} props.height - Height of the upload area (default: "h-48")
 * @param {string} props.previewHeight - Height of the preview image (default: "h-64")
 */
export function ImageUploader({
  id,
  preview,
  onImageSelect,
  onImageRemove,
  disabled = false,
  className = "",
  maxSizeMB = 5,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.8,
  compress = true,
  height = "h-48",
  previewHeight = "h-64",
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset error
      setError(null);

      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }

      // Validate file size (before compression)
      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes * 2) {
        // Allow 2x max size before compression
        setError(`Image is too large. Maximum size is ${maxSizeMB * 2}MB`);
        return;
      }

      setIsProcessing(true);

      try {
        let processedFile = file;

        // Compress image if enabled
        if (compress) {
          console.log(`[ImageUploader] Original: ${file.name} - ${(file.size / 1024).toFixed(2)} KB`);
          
          const compressedBlob = await compressImage(file, {
            maxWidth,
            maxHeight,
            quality,
          });

          // Only use compressed version if it's actually smaller
          if (compressedBlob.size < file.size) {
            processedFile = new File([compressedBlob], file.name, {
              type: compressedBlob.type,
            });
            const savings = ((1 - processedFile.size / file.size) * 100).toFixed(1);
            console.log(`[ImageUploader] Compressed: ${processedFile.name} - ${(processedFile.size / 1024).toFixed(2)} KB (${savings}% smaller)`);
          } else {
            console.log(`[ImageUploader] Skipped compression - original is smaller or equal`);
          }
        }

        // Check final size
        if (processedFile.size > maxBytes) {
          setError(`Image is too large after compression. Maximum size is ${maxSizeMB}MB`);
          setIsProcessing(false);
          return;
        }

        // Create preview URL
        const previewUrl = URL.createObjectURL(processedFile);

        // Call the callback with the processed file and preview
        onImageSelect(processedFile, previewUrl);
      } catch (err) {
        console.error("Image processing error:", err);
        setError("Failed to process image. Please try another file.");
      } finally {
        setIsProcessing(false);
        // Reset input value so the same file can be selected again
        e.target.value = "";
      }
    },
    [compress, maxHeight, maxSizeMB, maxWidth, onImageSelect, quality]
  );

  const handleRemove = useCallback(() => {
    setError(null);
    onImageRemove();
  }, [onImageRemove]);

  const isDisabled = disabled || isProcessing;

  return (
    <div className={`space-y-2 ${className}`}>
      {preview ? (
        <div className="group bg-muted relative overflow-hidden rounded-lg border">
          <img
            src={preview}
            alt="Preview"
            className={`${previewHeight} w-full object-cover transition-transform duration-300 group-hover:scale-105`}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 cursor-pointer">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={isDisabled}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Remove Image
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <label
            htmlFor={id}
            className={`hover:bg-muted/50 flex ${height} w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
              isDisabled ? "pointer-events-none opacity-50" : "border-muted-foreground/25"
            }`}
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {isProcessing ? (
                <>
                  <Loader2 className="text-muted-foreground mb-2 h-10 w-10 animate-spin" />
                  <p className="text-muted-foreground text-base font-medium">Processing...</p>
                </>
              ) : (
                <>
                  <ImageIcon className="text-muted-foreground mb-2 h-10 w-10" />
                  <p className="text-muted-foreground text-base font-medium">Click to upload image</p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Max {maxSizeMB}MB (JPG, PNG, WEBP)
                  </p>
                </>
              )}
            </div>
            <Input
              id={id}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
              disabled={isDisabled}
            />
          </label>
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}

/**
 * Utility function to convert a File to base64
 * Can be used when preparing data for API submission
 */
export const fileToBase64 = blobToBase64;

/**
 * Utility function to compress an image file
 * Can be used externally if needed
 */
export { compressImage };

export default ImageUploader;
